import { RoadsideInventory } from "@l2r-front/l2r-proptypes"

// Helper function to get the direction of a feature
export function getFeatureDirection(feature: GeoJSON.Feature): string {
    const props = feature.properties
    if (props?.["direction"]) {
        return props["direction"]
    }
    const linearLocation = props?.["linearLocation"]?.[0] || props?.["linear_location"]?.[0]
    if (linearLocation) {
        return linearLocation.start < linearLocation.end ? "+" : "-"
    }
    return "+"
}

// Helper function to get linear location from a feature
export function getLinearLocation(feature: GeoJSON.Feature): { road: string, start: number, end: number } | null {
    const linearLocation = feature.properties?.["linearLocation"]?.[0] || feature.properties?.["linear_location"]?.[0]
    if (!linearLocation) return null
    return {
        road: linearLocation.road,
        start: linearLocation.start,
        end: linearLocation.end,
    }
}

// Helper function to normalize a range (ensure start < end)
export function normalizeRange(start: number, end: number): [number, number] {
    return start < end ? [Math.floor(start), Math.ceil(end)] : [Math.floor(end), Math.ceil(start)]
}

// Helper function to check if two ranges overlap
export function rangesOverlap(range1: [number, number], range2: [number, number]): boolean {
    return range1[0] < range2[1] && range2[0] < range1[1]
}

// Helper function to compute the intersection of two ranges
export function intersectRanges(range1: [number, number], range2: [number, number]): [number, number] | null {
    const start = Math.max(range1[0], range2[0])
    const end = Math.min(range1[1], range2[1])
    if (start < end) {
        return [start, end]
    }
    return null
}

// Helper function to subtract a range from another range, returning remaining parts
export function subtractRange(baseRange: [number, number], toSubtract: [number, number]): [number, number][] {
    const result: [number, number][] = []

    // No overlap - return the base range unchanged
    if (!rangesOverlap(baseRange, toSubtract)) {
        return [baseRange]
    }

    // Part before the subtracted range
    if (baseRange[0] < toSubtract[0]) {
        result.push([baseRange[0], toSubtract[0]])
    }

    // Part after the subtracted range
    if (baseRange[1] > toSubtract[1]) {
        result.push([toSubtract[1], baseRange[1]])
    }

    return result
}

// Helper function to subtract multiple ranges from a single range
export function subtractMultipleRanges(baseRange: [number, number], rangesToSubtract: [number, number][]): [number, number][] {
    let remainingRanges: [number, number][] = [baseRange]

    for (const rangeToSubtract of rangesToSubtract) {
        const newRemainingRanges: [number, number][] = []
        for (const remaining of remainingRanges) {
            newRemainingRanges.push(...subtractRange(remaining, rangeToSubtract))
        }
        remainingRanges = newRemainingRanges
    }

    return remainingRanges.filter(r => r[0] < r[1])
}

// Helper function to create a RoadsideInventoryItem
export function createInventoryItem(
    road: string,
    start: number,
    end: number,
    direction: string,
    roadsideInventoryType: string,
    condition: number
): RoadsideInventory {
    // Ensure start < end in the final object
    const [normalizedStart, normalizedEnd] = normalizeRange(start, end)

    return {
        linear_location: [{
            typeid: 1,
            road,
            start: normalizedStart,
            end: normalizedEnd,
        }],
        roadside_inventory_type: roadsideInventoryType,
        direction,
        condition,
    }
}

// Helper type for grouped features
export type GroupedFeature = {
    feature: GeoJSON.Feature,
    linearLoc: { road: string, start: number, end: number },
    normalizedRange: [number, number]
}

// Helper function to group features by direction
export function groupFeaturesByDirection(features: GeoJSON.Feature[]): Record<string, GroupedFeature[]> {
    const grouped: Record<string, GroupedFeature[]> = {
        "+": [],
        "-": [],
    }

    for (const feature of features) {
        const linearLoc = getLinearLocation(feature)
        if (!linearLoc) continue

        const direction = getFeatureDirection(feature)
        const normalizedRange = normalizeRange(linearLoc.start, linearLoc.end)

        grouped[direction].push({ feature, linearLoc, normalizedRange })
    }

    return grouped
}

// Helper function to extract normalized linear locations from features as strings for comparison
export function extractNormalizedLinearLocations(features: GeoJSON.Feature[], includeCondition = false): string[] {
    return features
        .map(feature => {
            const linearLoc = getLinearLocation(feature)
            const direction = getFeatureDirection(feature)
            if (!linearLoc) return null
            const [normalizedStart, normalizedEnd] = normalizeRange(linearLoc.start, linearLoc.end)
            const base = `${linearLoc.road}|${normalizedStart}|${normalizedEnd}|${direction}`
            return includeCondition ? `${base}|${feature.properties?.["condition"] ?? ""}` : base
        })
        .filter((loc): loc is string => loc !== null)
        .sort()
}

// Helper function to extract normalized linear locations from inventory items as strings for comparison
export function extractInventoryLinearLocations(items: RoadsideInventory[], typeCode: string, includeCondition = false): string[] {
    return items
        .filter(item => item.roadside_inventory_type === typeCode)
        .map(item => {
            const linearLoc = item.linear_location?.[0]
            if (!linearLoc) return null
            const base = `${linearLoc.road}|${linearLoc.start}|${linearLoc.end}|${item.direction}`
            return includeCondition ? `${base}|${item.condition ?? ""}` : base
        })
        .filter((loc): loc is string => loc !== null)
        .sort()
}

// Helper function to check if features have changed compared to original
export function haveFeaturesChanged(
    originalFeatures: GeoJSON.Feature[],
    resultItems: RoadsideInventory[],
    typeCode: string,
    checkCondition = false
): boolean {
    const originalLinearLocations = extractNormalizedLinearLocations(originalFeatures, checkCondition)
    const resultLinearLocations = extractInventoryLinearLocations(resultItems, typeCode, checkCondition)

    if (originalLinearLocations.length !== resultLinearLocations.length) {
        return true
    }

    return originalLinearLocations.some((loc, index) => loc !== resultLinearLocations[index])
}
